import { Autocomplete, Box, Card, Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { FormProvider, RHFTextField, RHFUploadAvatar } from '../../fl-components/hook-form';
import Label from '../../fl-components/Label';
import { fData } from '../../utils/formatNumber';
import { ConvertToBase64 } from '../../helper/commonHelper';
import { uploadFile } from '../../service/upload.file.service';
import { updateOrganization } from '../../service/org.service';
import { CHANGE_SELECTED_ORG } from '../../redux/constants';

export default function InstituteDialogContent({ handleClose, setFormSubmitting }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedPicture, setPicture] = useState(null);
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const currentUser = useSelector((state) => state.auth.currentUser);

  const stateData = useSelector((state) => state.geo.states);
  const cityData = useSelector((state) => state.geo.cities);

  const NewUserSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    address: Yup.string().required('Address is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    // avatarUrl: Yup.mixed().test('required', 'Avatar is required', (value) => value !== ''),
    zipCode: Yup.string().length(6).required('Zipcode is required'),
    email: Yup.string().required('Email is required').email(),
    website: Yup.string().required('Website is required').url()
  });

  const defaultValues = useMemo(
    () => ({
      name: selectedOrg?.name || '',
      avatarUrl: selectedOrg?.instituteProfileImage || '',
      city: selectedOrg?.city || '',
      state: selectedOrg?.state || '',
      address: selectedOrg?.address || '',
      zipCode: selectedOrg?.zipCode || '',
      email: selectedOrg?.email || '',
      website: selectedOrg?.website || ''
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrg, cityData]
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues
  });

  const { setValue, handleSubmit, getValues, control } = methods;

  const uploadProfilePicture = async () => {
    let profileUrl = '';
    if (selectedPicture) {
      const base64 = await ConvertToBase64(selectedPicture);
      const fileObj = {
        base64,
        fileType: selectedPicture.type,
        fileName: selectedPicture.name,
        uploadFor: 'instituteProfileImage',
        userId: currentUser.id
      };

      const res = await uploadFile('', fileObj);
      if (res) {
        profileUrl = res.fileURL;
      }
    } else {
      profileUrl = getValues('avatarUrl');
    }

    return profileUrl;
  };

  const onSubmit = async () => {
    try {
      const instProfile = await uploadProfilePicture();
      setFormSubmitting(true);
      const orgObj = {
        name: getValues('name').trim()
      };
      if (getValues('city')) orgObj.city = getValues('city');
      if (getValues('state')) orgObj.state = getValues('state');
      if (getValues('address')) orgObj.address = getValues('address');
      if (getValues('zipCode')) orgObj.zipCode = getValues('zipCode');
      if (getValues('website')) orgObj.website = getValues('website');
      if (getValues('email')) orgObj.email = getValues('email');
      if (instProfile) orgObj.instituteProfileImage = instProfile;

      const orgRes = await updateOrganization(selectedOrg?.id ?? selectedOrg?.orgId, orgObj);
      if (orgRes) {
        dispatch({ type: CHANGE_SELECTED_ORG, payload: orgRes });
        enqueueSnackbar('Institute Profile Updated Successfully');
        setFormSubmitting(false);
        handleClose();
      }
    } catch (error) {
      console.error(error);
      setFormSubmitting(false);
    }
  };

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (file) {
        setPicture(file);
        setValue(
          'avatarUrl',
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        );
      }
    },
    [setValue]
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ py: 10, px: 3 }}>
            <Label
              color="success"
              sx={{ textTransform: 'uppercase', position: 'absolute', top: 24, right: 24 }}
            >
              active
            </Label>

            <Box sx={{ mb: 5 }}>
              <RHFUploadAvatar
                name="avatarUrl"
                accept="image/*"
                maxSize={3145728}
                onDrop={handleDrop}
                helperText={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    Allowed *.jpeg, *.jpg, *.png
                    <br /> max size of {fData(3145728)}
                  </Typography>
                }
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' }
              }}
            >
              <RHFTextField name="name" label="Institute Name" />

              <Controller
                name="state"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    // {...field}
                    options={stateData}
                    value={getValues('state')}
                    onChange={(event, newValue) => {
                      field.onChange(newValue?.name || '');
                    }}
                    getOptionLabel={(option) => option.name || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="city"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    // {...field}
                    options={cityData}
                    value={getValues('city')}
                    onChange={(event, newValue) => {
                      field.onChange(newValue?.name || '');
                    }}
                    getOptionLabel={(option) => option.name || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
              <RHFTextField name="address" label="Address" />
              <RHFTextField name="zipCode" label="Zip/Code" />
              <RHFTextField name="email" label="Email" />
              <RHFTextField name="website" label="Website" />
            </Box>

            {/* <Stack alignItems="flex-end" sx={{ mt: 3 }} justifyContent="end" flex={1}> */}
            <LoadingButton
              sx={{ display: 'none' }}
              id="instituteSaveButton"
              type="submit"
              variant="contained"
            >
              Save Changes
            </LoadingButton>
            {/* </Stack> */}
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

InstituteDialogContent.propTypes = {
  handleClose: PropTypes.func,
  setFormSubmitting: PropTypes.func
};
